import { camelizeNestedKeys } from '@cex/shared-modules';
import { mainEmptySplitApi } from '@/features/main/mainApi';
import {
  Challenge,
  Crypto,
  CryptoResponse,
  CryptoWithdrawalPayload,
  CryptoWithdrawalPayloadResponse,
  CryptoWithdrawals,
  CryptoWithdrawalsPayload,
  CryptoWithdrawalResponse,
  CryptoWithdrawalsResponse,
  CryptoWithdrawalTANResponse,
  Transaction,
  TransactionsPayload,
  TransactionsResponse,
  CryptoDepositsPayload
} from '@/types/crypto';
import { keysToSnakeCase } from '@/types/formatters';
import { balancesApi } from '@/features/balance/balancesApi';
import { Whitelist, Whitelists } from '@/types/whitelist';
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';

const cryptoDepositsAdapter = createEntityAdapter<any>({
  selectId: (deposit) => deposit.uuid
});
const initialState = cryptoDepositsAdapter.getInitialState();

export const cryptoApi = mainEmptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getCryptoDepositAddress: builder.query<Crypto, string>({
      query: (id) => ({
        url: `/api/v2/crypto/addresses`,
        method: 'POST',
        body: { asset_id: id }
      }),
      transformResponse: (res: CryptoResponse) => {
        return res.data;
      }
    }),

    getCryptoWithdrawals: builder.query<CryptoWithdrawals, CryptoWithdrawalsPayload>({
      query: ({ assetId, sortOrder = 'asc' }) => ({
        url: `/api/v2/crypto/withdrawals`,
        params: {
          asset_id: assetId,
          'order[created_at]': sortOrder
        }
      }),
      transformResponse: (res: CryptoWithdrawalsResponse) => {
        return camelizeNestedKeys(res.data);
      },
      providesTags: (result) =>
        result
          ? [...result.map(({ assetId }) => ({ type: 'Withdrawals' as const, id: assetId })), 'Withdrawals']
          : ['Withdrawals']
    }),

    getCryptoDeposits: builder.query<any, CryptoDepositsPayload | void>({
      query: (args) => {
        const { assetId, sortOrder = 'desc', requiresTravelRuleOnly } = args || {};
        const params: Record<string, string> = {};

        if (assetId) {
          params.asset_id = assetId;
        }
        if (sortOrder) {
          params['order[created_at]'] = sortOrder;
        }
        if (requiresTravelRuleOnly) {
          params.transaction_state = 'requirestravelruleinformation';
        }

        return {
          url: '/api/v2/crypto/deposits',
          params
        };
      },
      transformResponse(response: any) {
        const data = camelizeNestedKeys(response.data);
        return cryptoDepositsAdapter.setAll(initialState, data);
      },
      providesTags: () => ['CryptoDeposits']
    }),

    withdrawCrypto: builder.mutation<CryptoWithdrawalResponse, CryptoWithdrawalPayload>({
      query: ({ amount, asset_id, target_address, destination_tag }) => ({
        url: '/api/v2/crypto/withdrawals',
        method: 'POST',
        body: {
          amount: amount,
          asset_id: asset_id,
          destination_tag: destination_tag,
          target_address: target_address
        }
      }),
      transformResponse: (res: { data: { withdrawal: CryptoWithdrawalResponse } }) => {
        return camelizeNestedKeys(res.data.withdrawal);
      },
      onQueryStarted: async (arg, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Something went wrong with withdraw.');
        }
      }
    }),

    getWithdrawCryptoChallenge: builder.mutation<CryptoWithdrawalTANResponse, any>({
      query: (id) => ({
        url: `/api/v2/crypto/withdrawals/${id}/2fa_challenges`,
        method: 'POST'
      }),
      transformResponse: (res: { data: { challenge: Challenge<CryptoWithdrawalTANResponse> } }) => {
        return camelizeNestedKeys(res.data.challenge);
      }
    }),

    getWhitelist: builder.query<Whitelist, void>({
      query: () => ({
        url: `/api/v2/crypto/target_addresses`
      }),
      transformResponse: (res: Whitelists) => {
        return camelizeNestedKeys(res.data);
      }
    }),

    verifyTwoFactor: builder.mutation<any, any>({
      query: ({
        payload,
        twoFactorCode,
        challengeId
      }: {
        payload: CryptoWithdrawalPayloadResponse;
        twoFactorCode: string;
        challengeId: string;
      }) => ({
        url: `/api/v2/crypto/withdrawals/${challengeId}/execute`,
        method: 'POST',
        body: {
          challenge: {
            purpose: 'v2/crypto/withdrawal',
            payload: keysToSnakeCase(payload),
            tan: twoFactorCode
          }
        }
      }),
      // workaround is currently necessary because the 'Balances' tag is in a different API service
      // https://stackoverflow.com/questions/69243083/rtk-query-invalidate-cache-of-an-api-service-from-another-api-service
      async onQueryStarted(_, { dispatch, queryFulfilled }): Promise<void> {
        await queryFulfilled;
        dispatch(balancesApi.util.invalidateTags(['Balances']));
      },
      transformResponse: (res: any) => {
        return camelizeNestedKeys(res.data);
      },
      invalidatesTags: (result) => (result === undefined ? [] : [{ type: 'Withdrawals', id: result.assetId }])
    }),

    getTransactions: builder.query<Transaction[], TransactionsPayload>({
      query: ({ page, sortOrder = 'asc' }) => ({
        url: `/api/v2/crypto/transactions`,
        params: {
          'order[created_at]': sortOrder,
          page: page
        }
      }),
      // to avoid multiple retries when the query fails
      // currently necessary because we don't know the max number of pages and simply query the first 5
      // thus we obtain a lot of 404s from the backend
      extraOptions: { maxRetries: 1 },
      transformResponse: async (res: TransactionsResponse) => {
        return camelizeNestedKeys(res.data);
      },
      providesTags: (res, err, arg) => [{ type: 'Transactions', id: arg.page }, 'Transactions']
    })
  })
});

export const selectCryptoDepositsResult = cryptoApi.endpoints.getCryptoDeposits.select();
const selectCryptoDepositsData = createSelector(selectCryptoDepositsResult, (apiSlice) => apiSlice.data);

export const { selectAll: selectAllCryptoDeposits, selectById: selectCryptoDepositById } =
  cryptoDepositsAdapter.getSelectors((state: RootState) => selectCryptoDepositsData(state) ?? initialState);

export const selectCryptoDepositsByAssetId = (assetId: string) =>
  createSelector([selectAllCryptoDeposits], (allDeposits) =>
    allDeposits.filter((deposit) => deposit.assetId === assetId)
  );

export const selectCryptoDepositsByState = (transactionState: string) =>
  createSelector([selectAllCryptoDeposits], (allDeposits) =>
    allDeposits.filter((deposit) => deposit.transactionState === transactionState)
  );

export const selectCryptoDepositsByAssetIdAndState = createSelector(
  [
    (state: RootState) => selectAllCryptoDeposits(state),
    (state: RootState, assetId: string) => assetId,
    (state: RootState, assetId: string, transactionState: string) => transactionState
  ],
  (allDeposits, assetId, transactionState) =>
    allDeposits.filter((deposit) => deposit.assetId === assetId && deposit.transactionState === transactionState)
);

export const selectCryptoDepositsNeedingTravelRule = createSelector([selectAllCryptoDeposits], (allDeposits) =>
  allDeposits.filter(
    (deposit) =>
      deposit.depositIntentId &&
      deposit.depositIntentId.trim().length > 0 &&
      deposit.transactionState === 'requirestravelruleinformation'
  )
);

export const {
  useGetCryptoDepositAddressQuery,
  useGetCryptoWithdrawalsQuery,
  useGetWithdrawCryptoChallengeMutation,
  useGetTransactionsQuery,
  useGetWhitelistQuery,
  useWithdrawCryptoMutation,
  useVerifyTwoFactorMutation,
  useGetCryptoDepositsQuery
} = cryptoApi;
